import React, { useRef, useState } from "react";
import Chart from "react-apexcharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const BookingTrendsSection = ({
  comparisonYears,
  selectedHotel,
  availableHotels,
  selectedYear,
  yearList,
  selectedChartType,
  handleHotelChange,
  handleYearChange,
  handleChartTypeChange,
  chartOptions,
  chartSeries,
}) => {
  const exportRef = useRef(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const chartKey = [
    "booking-trends",
    selectedHotel || "all",
    selectedYear,
    selectedChartType,
    chartSeries.map((series) => `${series.name}:${series.data.join(",")}`).join("|"),
  ].join("-");

  const commonOptions = {
    colors: chartOptions.colors,
    dataLabels: chartOptions.dataLabels,
    xaxis: chartOptions.xaxis,
    yaxis: chartOptions.yaxis,
  };

  const resolvedChartOptions =
    selectedChartType === "bar"
      ? {
          ...commonOptions,
          chart: {
            ...chartOptions.chart,
            type: "bar",
          },
          plotOptions: {
            bar: {
              columnWidth: "55%",
              borderRadius: 4,
            },
          },
          stroke: {
            width: 0,
          },
          markers: {
            size: 0,
          },
        }
      : {
          ...chartOptions,
          chart: {
            ...chartOptions.chart,
            type: "line",
          },
        };

  const handleExportPdf = async () => {
    if (!exportRef.current || isExportingPdf) return;

    setIsExportingPdf(true);

    try {
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        ignoreElements: (element) => element.classList?.contains("pdf-export-ignore"),
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("l", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let remainingHeight = imgHeight;
      let position = margin;

      pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      remainingHeight -= pageHeight - margin * 2;

      while (remainingHeight > 0) {
        position -= pageHeight - margin * 2;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
        remainingHeight -= pageHeight - margin * 2;
      }

      const hotelName =
        availableHotels.find((hotel) => String(hotel.PropertyID) === String(selectedHotel))?.UnitName ||
        "All-Hotels";
      const safeHotelName = hotelName.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");

      pdf.save(`Booking-Trends-${safeHotelName}-${selectedYear}.pdf`);
    } catch (error) {
      console.error("Failed to export booking trends PDF:", error);
      window.alert("Failed to export booking trends PDF. Please try again.");
    } finally {
      setIsExportingPdf(false);
    }
  };

  return (
    <div className="row g-3">
      <div className="col-md-12">
        <div className="card" ref={exportRef}>
          <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
            <h6 className="card-title mb-0">
              Booking Trends ({comparisonYears.current} vs {comparisonYears.previous})
            </h6>
            <button
              type="button"
              className="btn btn-danger btn-sm pdf-export-ignore"
              onClick={handleExportPdf}
              disabled={isExportingPdf}
              title="Export PDF"
            >
              <i className="bi bi-filetype-pdf me-1"></i>
              {isExportingPdf ? "Exporting..." : "Export PDF"}
            </button>
          </div>
          <div className="card-body">
            <div className="row mb-3 align-items-end">
              <div className="col-md-6 col-lg-3">
                <label className="form-label">Select Hotel</label>
                <select
                  className="form-select"
                  value={selectedHotel}
                  onChange={handleHotelChange}
                >
                  <option value="">-- Select Hotel --</option>
                  {availableHotels.map((hotel) => (
                    <option key={hotel.PropertyID} value={hotel.PropertyID}>
                      {hotel.UnitName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 col-lg-3">
                <label className="form-label">Select Year</label>
                <select className="form-select" value={selectedYear} onChange={handleYearChange}>
                  {yearList.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 col-lg-3">
                <label className="form-label">Graph Type</label>
                <select
                  className="form-select"
                  value={selectedChartType}
                  onChange={handleChartTypeChange}
                >
                  <option value="line">Line Chart</option>
                  <option value="bar">Bar Chart</option>
                </select>
              </div>
            </div>

            <div id="reservations">
              <Chart
                key={chartKey}
                options={resolvedChartOptions}
                series={chartSeries}
                height={resolvedChartOptions.chart.height}
                type={selectedChartType}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
